/**
 * Validates if an email is a valid student email (.edu domain)
 * @param email - The email address to validate
 * @returns Object containing validation result and error message if invalid
 */
export interface EmailValidationResult {
  isValid: boolean;
  error?: string;
}

export const validateStudentEmail = (email: string): EmailValidationResult => {
  // Check if email is provided
  if (!email || email.trim() === '') {
    return {
      isValid: false,
      error: 'Email is required'
    };
  }

  // Basic email regex pattern
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  
  // Check if email format is valid
  if (!emailRegex.test(email.trim())) {
    return {
      isValid: false,
      error: 'Please enter a valid email address'
    };
  }

  // Check if email ends with .edu
  const trimmedEmail = email.trim().toLowerCase();
  if (!trimmedEmail.endsWith('.edu')) {
    return {
      isValid: false,
      error: 'Please use your student email address ending with .edu'
    };
  }

  // Additional validation for common .edu patterns
  const eduDomainRegex = /^[^\s@]+@[^\s@]+\.edu$/;
  if (!eduDomainRegex.test(trimmedEmail)) {
    return {
      isValid: false,
      error: 'Invalid .edu email format'
    };
  }

  return {
    isValid: true
  };
};

/**
 * Extracts the university name from a .edu email address
 * @param email - The validated .edu email address
 * @returns The university domain or null if invalid
 */
export const extractUniversityDomain = (email: string): string | null => {
  const validation = validateStudentEmail(email);
  if (!validation.isValid) {
    return null;
  }

  const domain = email.trim().toLowerCase().split('@')[1];
  return domain || null;
};

/**
 * Common .edu domains for additional validation (optional)
 */
export const COMMON_EDU_DOMAINS = [
  'harvard.edu',
  'mit.edu',
  'stanford.edu',
  'berkeley.edu',
  'ucla.edu',
  'umich.edu',
  'upenn.edu',
  'yale.edu',
  'princeton.edu',
  'columbia.edu',
  'uchicago.edu',
  'nyu.edu',
  'utexas.edu',
  'usc.edu',
  'gatech.edu'
];

/**
 * Checks if the email is from a recognized university
 * @param email - The email to check
 * @returns Whether the domain is in the common edu domains list
 */
export const isRecognizedUniversity = (email: string): boolean => {
  const domain = extractUniversityDomain(email);
  if (!domain) return false;
  
  return COMMON_EDU_DOMAINS.includes(domain);
};